import { useQuery } from '@apollo/client';
import { ErrorMessage } from '@hookform/error-message';
import { compact, isEqual } from 'lodash';
import { ReactNode, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Control, FieldPathByValue, FieldValues, useController } from 'react-hook-form';

import { GetContactsForLoanDocument } from '@willow/graphql-iso/src/app';
import { getBorrowerNameWithoutMiddleName } from '@willow/shared-iso';
import { CompanyId, LoanId, PaymentPayer, ThirdPartyPaymentPayer } from '@willow/types-iso';

import { assertNever, formatThirdPartyContactType } from '../../../utils';
import { Loader } from '../../Loader/Loader';
import { Options, WillowSelect } from './WillowSelect';

type Props<T extends FieldValues> = {
  companyId: CompanyId;
  loanId: LoanId;
  fieldName: FieldPathByValue<T, PaymentPayer | undefined>;
  control: Control<T>;
  hideErrorMessage?: boolean;
  label?: ReactNode;
  placeholder?: string;
  required?: boolean;
  disableThirdParty?: boolean;
};

export function ControlledLoanContactSingleSelect<T extends FieldValues>({
  companyId,
  loanId,
  control,
  fieldName,
  hideErrorMessage = false,
  label,
  placeholder,
  required,
  disableThirdParty,
}: Props<T>) {
  const {
    formState: { errors },
    field: { onChange, value },
  } = useController({ control, name: fieldName });

  const { loading, data } = useQuery(GetContactsForLoanDocument, {
    variables: { companyId, loanId },
  });

  const [otherThirdParty, setOtherThirdParty] = useState<ThirdPartyPaymentPayer>({
    type: 'thirdParty',
    name: '',
  });

  const contacts = useMemo(() => {
    if (data == null) {
      return [];
    }
    const {
      company: {
        loan: { primaryBorrower, additionalBorrowers, entity, authorizedContacts },
      },
    } = data;
    return compact([primaryBorrower, ...additionalBorrowers, entity, ...authorizedContacts]);
  }, [data]);

  const OPTIONS: Options = useMemo(
    () =>
      compact([
        ...contacts.map((contact) => {
          let label: string;
          let value: PaymentPayer;
          switch (contact.__typename) {
            case 'ThirdPartyAuthorizedContact': {
              label = `${contact.name.firstName} ${contact.name.lastName} (${formatThirdPartyContactType(
                contact.type,
              )})`;
              value = {
                id: contact.id,
                type: 'authorizedContact' as const,
              };
              break;
            }
            case 'Borrower': {
              label = getBorrowerNameWithoutMiddleName(contact);
              value = {
                id: contact.id,
                type: 'borrower' as const,
              };
              break;
            }
            case 'BuilderAuthorizedContact': {
              label = `${contact.builderCompanyName} (Builder)`;
              value = {
                id: contact.id,
                type: 'authorizedContact' as const,
              };
              break;
            }
            case 'Entity': {
              label = `${contact.name} (Entity)`;
              value = {
                id: contact.id,
                type: 'entity' as const,
              };
              break;
            }
            default:
              assertNever(contact);
          }
          return {
            label,
            value,
          };
        }),
        !disableThirdParty && {
          label: 'Other',
          value: otherThirdParty,
        },
      ]),
    [contacts, disableThirdParty, otherThirdParty],
  );

  if (loading || data == null) {
    return <Loader />;
  }

  return (
    <>
      {label && (
        <Form.Label className="u-fs-2 fw-bold">
          {label}
          {required && <span className="u-color-red1 ps-1 ">*</span>}
        </Form.Label>
      )}

      <WillowSelect
        placeholder={placeholder || 'Select contact'}
        options={OPTIONS}
        value={
          value
            ? OPTIONS.filter((r) => isEqual(value, r.value))
            : value.type === 'thirdParty'
            ? OPTIONS.filter((r) => r.type === 'thirdParty')
            : null
        }
        isClearable={!required}
        onChange={(e) => onChange(e?.value ?? '')}
      />

      {value.type === 'thirdParty' && (
        <>
          <Form.Label className="u-fs-2 mt-2 fw-bold">
            Name
            {required && <span className="u-color-red1 ps-1 ">*</span>}
          </Form.Label>

          <Form.Control
            type="text"
            placeholder="Enter name"
            onChange={(e) => {
              const thirdParty = { type: 'thirdParty' as const, name: e.target.value };
              setOtherThirdParty(thirdParty);
              onChange(thirdParty);
            }}
          />
        </>
      )}

      {!hideErrorMessage && (
        <ErrorMessage
          errors={errors}
          name={fieldName as any}
          render={({ message }) => <div className="u-fs-2 u-color-red1">{message}</div>}
        />
      )}
    </>
  );
}
